import { Enums as CoreEnums, metaData } from "@cornerstonejs/core";

/**
 * Convert image URLs to Cornerstone imageIds
 */
export function buildImageIds(imageUrls = []) {
  return imageUrls.map((url) =>
    url.startsWith("wadouri:") ? url : `wadouri:${url}`
  );
}

/**
 * Unique volume id per series
 */
 export function buildVolumeId(series) {
  if (!series) return null;
  
  // Return seriesInstanceUID, id, or a fallback string
  const id = series.seriesInstanceUID || series.id || series.seriesNumber;
  if (!id) return null;
  
  return `cornerstoneStreamingImageVolume:${id}`;
}

/**
 * Returns viewport orientation
 */
 export function getViewportOrientation(
    mprView,
    panelIndex
  ) {
    switch (mprView) {
  
      case "AXIAL":
        return CoreEnums.OrientationAxis.AXIAL;
  
      case "SAGITTAL":
        return CoreEnums.OrientationAxis.SAGITTAL;
  
      case "CORONAL":
        return CoreEnums.OrientationAxis.CORONAL;
  
      case "THREE_VIEW":
  
        switch (panelIndex) {
  
          case 0:
            return CoreEnums.OrientationAxis.AXIAL;
  
          case 1:
            return CoreEnums.OrientationAxis.SAGITTAL;
  
          case 2:
            return CoreEnums.OrientationAxis.CORONAL;
  
          default:
            return CoreEnums.OrientationAxis.AXIAL;
        }
  
      default:
        return CoreEnums.OrientationAxis.AXIAL;
    }
  }

/**
 * Slice count for each orientation
 */
export function getSliceCount(volume, orientation) {
  if (!volume?.dimensions) return 0;

  const [x, y, z] = volume.dimensions;

  switch (orientation) {
    case CoreEnums.OrientationAxis.AXIAL:
      return z;

    case CoreEnums.OrientationAxis.SAGITTAL:
      return x;

    case CoreEnums.OrientationAxis.CORONAL:
      return y;

    default:
      return z;
  }
}

/**
 * Read patient metadata
 */
export function getPatientInfo(imageId) {
  if (!imageId) return {};

  const patient =
    metaData.get("patientModule", imageId) || {};

  const study =
    metaData.get("generalStudyModule", imageId) || {};

  const series =
    metaData.get("generalSeriesModule", imageId) || {};

  const pixel =
    metaData.get("imagePixelModule", imageId) || {};

  const plane =
    metaData.get("imagePlaneModule", imageId) || {};

  const voi =
    metaData.get("voiLutModule", imageId) || {};

  let patientName = "";

  if (patient.patientName) {
    if (typeof patient.patientName === "object") {
      patientName = [
        patient.patientName.FamilyName,
        patient.patientName.GivenName,
        patient.patientName.MiddleName,
      ]
        .filter(Boolean)
        .join(" ");
    } else {
      patientName = String(patient.patientName)
        .replace(/\^/g, " ")
        .trim();
    }
  }

  return {
    patientId: patient.patientId || "",

    patientName,

    studyDesc:
      study.studyDescription ||
      series.seriesDescription ||
      "",

    seriesDesc:
      series.seriesDescription || "",

    windowCenter: Array.isArray(voi.windowCenter)
      ? voi.windowCenter[0]
      : voi.windowCenter || "",

    windowWidth: Array.isArray(voi.windowWidth)
      ? voi.windowWidth[0]
      : voi.windowWidth || "",

    imageSize:
      pixel.rows && pixel.columns
        ? `${pixel.columns} × ${pixel.rows}`
        : "",

    sliceThickness: plane.sliceThickness
      ? `${Number(plane.sliceThickness).toFixed(2)} mm`
      : "",
  };
}

/**
 * Move orthographic camera by one slice
 */
export function moveCameraSlice(viewport, direction = 1) {
  if (!viewport) return;

  const camera = viewport.getCamera?.();

  if (!camera?.viewPlaneNormal) return;

  const STEP = 1;

  const normal = camera.viewPlaneNormal;

  viewport.setCamera({
    position: camera.position.map(
      (v, i) => v + normal[i] * STEP * direction
    ),

    focalPoint: camera.focalPoint.map(
      (v, i) => v + normal[i] * STEP * direction
    ),
  });

  viewport.render();
}

/**
 * Middle slice index
 */
export function getMiddleSlice(totalSlices) {
  return Math.floor(totalSlices / 2);
}

/**
 * Clamp index
 */
export function clampSlice(index, totalSlices) {
  return Math.max(
    0,
    Math.min(index, totalSlices - 1)
  );
}